// packages/app/src/app/shared/user-management/UserDetails.tsx
"use client";

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Text, Input, Button, Box, Select } from "rizzui";
import FormGroup from "../../../../../app/shared/form-group";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useUser } from "../../../../../hooks/use-user";
import { updateUserKyc } from "../../../../../app/api/patch-kyc";

export default function UserDetails() {
  const KYC_OPTIONS = [
    "Success",
    "Pending",
    "Failed",
    "YetToStart",
    "Issued",
  ] as const;

  const { id } = useParams<{ id: string }>();
  const { data: user, isLoading, error } = useUser(id!);

  const [kycStatus, setKycStatus] = useState<
    "Success" | "Pending" | "Failed" | "YetToStart" | "Issued"
  >();
  useEffect(() => {
    if (user) setKycStatus(user.kycVerified);
  }, [user]);

  // editing state
  const [editOpen, setEditOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<
    "Success" | "Pending" | "Failed" | "YetToStart" | "Issued"
  >();
  // confirmation
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleConfirm = async () => {
    try {
      await updateUserKyc(id!, newStatus!);
      setKycStatus(newStatus);
    } catch (e) {
      console.error("Failed to patch KYC status", e);
    } finally {
      setConfirmOpen(false);
      setEditOpen(false);
    }
  };

  if (isLoading) return <div>Loading user…</div>;
  if (error) return <div className="text-red-600">Error: {error.message}</div>;
  if (!user) return <div>User not found.</div>;

  return (
    <div className="@container">
      <Link
        to="/user-management"
        className="flex gap-1 items-center w-fit mb-5 px-3 py-2 border rounded-full"
      >
        <IoIosArrowRoundBack />
        <Text>All Users</Text>
      </Link>

      <FormGroup
        title="User Info"
        description="View detailed information about the user."
        className="pt-7 @2xl:pt-9 @3xl:grid-cols-12 @3xl:pt-11"
      />

      <div className="mb-10 grid gap-7 divide-y divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11">
        <FormGroup title="First Name">
          <Input value={user.firstName} readOnly className="col-span-full" />
        </FormGroup>

        <FormGroup title="Middle Name">
          <Input
            value={user.middleName ?? "-"}
            readOnly
            className="col-span-full"
          />
        </FormGroup>

        <FormGroup title="Last Name">
          <Input value={user.lastName} readOnly className="col-span-full" />
        </FormGroup>

        <FormGroup title="Email Address">
          <Input value={user.email} readOnly className="col-span-full" />
        </FormGroup>

        <FormGroup title="Account Type">
          <Input value={user.accountType} readOnly className="col-span-full" />
        </FormGroup>

        <FormGroup title="Phone Number">
          <Input
            value={String(user.phoneNumber)}
            readOnly
            className="col-span-full"
          />
        </FormGroup>

        <FormGroup title="Country">
          <Input value={user.country} readOnly className="col-span-full" />
        </FormGroup>

        <FormGroup title="KYC Verified">
          <Input value={user.kycVerified} readOnly className="col-span-full" />
        </FormGroup>

        <FormGroup title="Created At">
          <Input
            value={new Date(user.createdAt).toLocaleString()}
            readOnly
            className="col-span-full"
          />
        </FormGroup>

        <FormGroup title="Actions">
          <Button
            onClick={() => {
              setNewStatus(kycStatus);
              setEditOpen(true);
            }}
          >
            Edit KYC Status
          </Button>
        </FormGroup>
      </div>
      {/* ——— Edit dialog ——— */}
      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <Box className="bg-white p-6 rounded shadow-lg w-full max-w-sm">
            <h3 className="text-lg font-medium mb-4">Edit KYC Status</h3>
            <p className="mb-2 text-sm text-gray-700">
              Current status: <strong>{kycStatus}</strong>
            </p>
            <Select
              options={KYC_OPTIONS.map((s) => ({ label: s, value: s }))}
              value={newStatus}
              onChange={(val: {
                label:
                  | "Success"
                  | "Pending"
                  | "Failed"
                  | "YetToStart"
                  | "Issued";
                value:
                  | "Success"
                  | "Pending"
                  | "Failed"
                  | "YetToStart"
                  | "Issued";
              }) => setNewStatus(val.value)}
              className="mb-4 w-full"
              dropdownClassName="!z-[10000]"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => setConfirmOpen(true)}>Change</Button>
            </div>
          </Box>
        </div>
      )}

      {/* ——— Confirmation dialog ——— */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <Box className="bg-white p-6 rounded shadow-lg w-full max-w-xs">
            <h4 className="text-md font-semibold mb-3">Are you sure?</h4>
            <p className="mb-4 text-sm">
              Change KYC status to <strong>{newStatus}</strong>?
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                No
              </Button>
              <Button color="danger" onClick={handleConfirm}>
                Yes, confirm
              </Button>
            </div>
          </Box>
        </div>
      )}
    </div>
  );
}
